import React, { useEffect, useMemo, useState } from 'react';
import { endOfDay, format, startOfDay } from 'date-fns';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Calendar } from '@/components/ui/calendar';
import { OccurrenceRow } from '@/components/teacher/schedule/calendar/OccurrenceRow';
import type { WeeklyItem } from '@/types/schedule';

interface Props { teacherCode?: string }

export function AdminDailySchedule({ teacherCode }: Props) {
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const dateKey = useMemo(() => format(selectedDate, 'yyyy-MM-dd'), [selectedDate]);
  const [items, setItems] = useState<WeeklyItem[]>([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!teacherCode) { setItems([]); return; }
      const start = startOfDay(selectedDate);
      const end = endOfDay(selectedDate);

      const { data: jobs, error } = await (supabase as any)
        .from('fixed_jobs_with_students')
        .select('id, subject, location, weekday, start_time, end_time, students')
        .eq('teacher_code', teacherCode)
        .eq('is_active', true);
      if (cancelled || error) { setItems([]); return; }

      const projected: WeeklyItem[] = [] as any;
      const dow = selectedDate.getDay();
      const dateStr = dateKey;
      for (const j of (jobs ?? []) as any[]) {
        if (j.weekday === dow) {
          projected.push({
            fixed_job_id: j.id,
            occurrence_id: null,
            date: dateStr,
            subject: j.subject,
            location: j.location,
            start_time: j.start_time,
            end_time: j.end_time,
            duration_minutes: 0,
            status: 'SCHEDULED',
            confirmed: false,
            edit_status: 'NONE',
            insufficient_hours: false,
            students: j.students ?? [],
            edit_request_payload: undefined,
            occurrence_edit_requests: [],
          });
        }
      }

      // Overlay occurrences on the selected date
      const { data: occ, error: occErr } = await (supabase as any)
        .from('job_occurrences')
        .select('id, fixed_job_id, occurrence_date, start_time, end_time, effective_duration_minutes, status, confirmed_by_teacher, edit_status, edit_request_payload')
        .gte('occurrence_date', dateStr)
        .lte('occurrence_date', dateStr);
      if (cancelled || occErr) { setItems(projected); return; }
      const occMap = new Map<string, any>();
      for (const o of occ ?? []) occMap.set(`${o.occurrence_date}|${o.fixed_job_id}`, o);
      const result: WeeklyItem[] = [];
      const seen = new Set<string>();
      for (const p of projected) {
        const key = `${p.date}|${p.fixed_job_id}`;
        const o = occMap.get(key);
        if (o) {
          seen.add(key);
          result.push({
            ...p,
            occurrence_id: o.id,
            start_time: o.start_time ?? p.start_time,
            end_time: o.end_time ?? p.end_time,
            duration_minutes: o.effective_duration_minutes ?? p.duration_minutes,
            status: o.status,
            confirmed: o.confirmed_by_teacher,
            edit_status: o.edit_status,
            edit_request_payload: o.edit_request_payload ?? undefined,
          });
        } else {
          result.push(p);
        }
      }
      // Include any ad-hoc occurrences without fixed_job projection (edge cases)
      for (const o of occ ?? []) {
        const key = `${o.occurrence_date}|${o.fixed_job_id}`;
        if (!seen.has(key)) {
          result.push({
            fixed_job_id: o.fixed_job_id,
            occurrence_id: o.id,
            date: o.occurrence_date,
            subject: '',
            location: '',
            start_time: o.start_time,
            end_time: o.end_time,
            duration_minutes: o.effective_duration_minutes ?? 0,
            status: o.status,
            confirmed: o.confirmed_by_teacher,
            edit_status: o.edit_status,
            insufficient_hours: false,
            occurrence_edit_requests: [],
          } as any);
        }
      }
      setItems(result.sort((a,b) => a.start_time.localeCompare(b.start_time)));
    })();
    return () => { cancelled = true; };
  }, [teacherCode, selectedDate, dateKey]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <Card className="lg:col-span-1">
        <CardHeader>
          <CardTitle>เลือกวัน</CardTitle>
        </CardHeader>
        <CardContent>
          <Calendar mode="single" selected={selectedDate} onSelect={(d) => d && setSelectedDate(d)} />
        </CardContent>
      </Card>
      <Card className="lg:col-span-2">
        <CardHeader>
          <CardTitle>ตารางวันที่ {dateKey}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {items.map((item) => (
              <OccurrenceRow
                key={`${item.fixed_job_id}-${item.date}`}
                item={item}
                teacherCode={teacherCode || ''}
              />
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}


